import { LoadingButton } from '@mui/lab'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useContent, useUserCompleteTask } from '../../../../logic'
import { useOptimisticUpdateNotificationAndOrderNotification } from '../../../../logic/optimisticUpdates'
import { useGetOrder, useGetPayloadMetaData } from '../../../../logic/useApiQuery'
import { getTransactionsTotal } from '../../../../utils/getTransactionsTotal'
import { isAdhocPortalOrder } from '../../../../utils/isAdhocPortalOrder'
import { usePayloadProcessingState } from '../../../context/payload-processing-state/PayloadProcessingState'
import { CompletePayloadTaskAmountRemainingAutoDialog } from '../../dialogs/CompletePayloadTaskAmountRemainingAutoDialog'
import { CompletePayloadTaskAmountRemainingDialog } from '../../dialogs/CompletePayloadTaskAmountRemainingDialog'
import { CompletePayloadTaskNoAmountRemainingDialog } from '../../dialogs/CompletePayloadTaskNoAmountRemainingDialog'
import { CompletePayloadTaskNoAmountSpecified } from '../../dialogs/CompletePayloadTaskNoAmountSpecified'
import { PaymentProcessingDialog } from '../../dialogs/PaymentProcessingDialog'
import { CashToCloseForm } from './CashToCloseForm'

export function CashToCloseEnterMoreOrComplete() {
  const { closingId, id } = useParams()
  const { search } = useLocation()
  const { TaskText } = useContent()

  const { mutate: mutateUserCompleteTask  } = useUserCompleteTask(closingId ?? '', id ?? '', search, {})
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  const { data: transactions } = useGetPayloadMetaData(closingId ?? '', id ?? '', search ?? '')
  const { data: orderData } = useGetOrder(closingId ?? '', search ?? '')
  const [payloadCompleteAmountRemainingOpen, setPayloadCompleteAmountRemainingOpen] = useState(false)
  const [payloadCompleteNoAmountRemainingOpen, setPayloadCompleteNoAmountRemainingOpen] = useState(false)
  const [payloadCompleteNoAmountSpecifiedOpen, setPayloadCompleteNoAmountSpecifiedOpen] = useState(false)
  const [payloadCompleteAmountRemainingAutoOpen, setPayloadCompleteAmountRemainingAutoOpen] = useState(false)
  const [processingDialogState, setProcessingDialogState] = useState<'closed' | 'processing' | 'success'>('closed')

  const onUserCompleteTask = () => {
    optimisticUpdateNotificationAndOrderNotification({ orderKey: closingId ?? '', notificationId: id ?? '', search, patch: { IsComplete: true }})
    mutateUserCompleteTask(undefined)
  }

  const cashToClosePaid = orderData && !isAdhocPortalOrder(orderData?.Order) && (Number(orderData?.Order.CashToCloseDetails?.amount) - getTransactionsTotal(transactions) <= 0)
  const cashToCloseAmountExists = orderData && !isAdhocPortalOrder(orderData?.Order) && !!orderData?.Order.CashToCloseDetails?.amount && Number(orderData?.Order.CashToCloseDetails?.amount) > 0
  const hasTransactions = (transactions?.length ?? 0) > 0
  const { payloadState, setPayloadState, processingAmount, setProcessingAmount } = usePayloadProcessingState()

  // Show the processing dialog when Payload enters the processing state
  useEffect(() => {
    if (payloadState === 'processing') {
      setProcessingDialogState('processing')
    }
  }, [payloadState])

  // When payment completes, transition the dialog to success state (don't immediately dismiss)
  useEffect(() => {
    if (payloadState !== 'completed') return
    setProcessingDialogState('success')
  }, [payloadState])

  // Close the processing dialog if there's an error (payloadState goes back to idle)
  // The error will be shown in the form via ErrorDialog
  useEffect(() => {
    if (payloadState === 'idle' && processingDialogState !== 'closed') {
      setProcessingDialogState('closed')
    }
  }, [payloadState, processingDialogState])

  // User closes the processing dialog (any method: Escape, backdrop, etc.)
  // If payment is still processing, it will reopen with success state when complete
  const handleProcessingDialogClose = useCallback(() => {
    setProcessingDialogState('closed')
  }, [])

  // User clicks "Continue" in the success dialog — dismiss it and show the appropriate completion dialog
  const handleProcessingDialogContinue = useCallback(() => {
    setProcessingDialogState('closed')
    setPayloadState('idle')
    setProcessingAmount(null)
    if (cashToClosePaid && cashToCloseAmountExists) {
      setPayloadCompleteNoAmountRemainingOpen(true)
    } else if (hasTransactions) {
      setPayloadCompleteAmountRemainingAutoOpen(true)
    }
  }, [cashToClosePaid, cashToCloseAmountExists, hasTransactions, setPayloadState, setProcessingAmount])

  const onUserSelfCompleteTask = () => {
    if (!cashToCloseAmountExists) {
      setPayloadCompleteNoAmountSpecifiedOpen(true)
    } else if (cashToClosePaid) {
      setPayloadCompleteNoAmountRemainingOpen(true)
    } else {
      setPayloadCompleteAmountRemainingOpen(true)
    }
  }


  return (
    <Box my={2}>
      <Box display="flex" flexDirection="column" alignItems="center">

        <Box mb={2}><CashToCloseForm/></Box>
        {/* Hide interactive elements while Payload is processing to prevent completing
            the task before submitPayloadPayment fires — clicking Complete during
            processing unmounts the form and kills the Payload event listener,
            losing the transaction. */}
        {payloadState === 'processing' ? (
          <Box display="flex" flexDirection="column" alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={24} aria-label="Processing payment" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing payment...
            </Typography>
          </Box>
        ) : (
          <LoadingButton
            variant={hasTransactions ? 'contained' : 'text'}
            color="primary"
            sx={{
              borderRadius: "40px",
              minWidth: "250px",
              mt: 1,
            }}
            onClick={onUserSelfCompleteTask}
          >
            Complete {TaskText}
          </LoadingButton>
        )}
      </Box>
      <CompletePayloadTaskNoAmountRemainingDialog
        open={payloadCompleteNoAmountRemainingOpen}
        setUserCompleteOpen={setPayloadCompleteNoAmountRemainingOpen}
        onUserCompleteTask={onUserCompleteTask}
        total={getTransactionsTotal(transactions)}
      />
      <CompletePayloadTaskAmountRemainingDialog
        open={payloadCompleteAmountRemainingOpen}
        setUserCompleteOpen={setPayloadCompleteAmountRemainingOpen}
        onUserCompleteTask={onUserCompleteTask}
        total={getTransactionsTotal(transactions)}
      />

      <CompletePayloadTaskNoAmountSpecified
        open={payloadCompleteNoAmountSpecifiedOpen}
        setUserCompleteOpen={setPayloadCompleteNoAmountSpecifiedOpen}
        onUserCompleteTask={onUserCompleteTask}
      />
      <CompletePayloadTaskAmountRemainingAutoDialog
        open={payloadCompleteAmountRemainingAutoOpen}
        setUserCompleteOpen={setPayloadCompleteAmountRemainingAutoOpen}
        onUserCompleteTask={onUserCompleteTask}
        total={getTransactionsTotal(transactions)}
      />
      <PaymentProcessingDialog
        open={processingDialogState !== 'closed'}
        state={processingDialogState === 'success' ? 'success' : 'processing'}
        amount={processingAmount}
        onClose={handleProcessingDialogClose}
        onContinue={handleProcessingDialogContinue}
      />
    </Box>
  )
}
